/**
 * Shared native detail routes registered in every tab stack.
 * Thin `app/(tabs)/…` files re-export screens from `src/native/navigation/`.
 *
 * Stack.Screen `name` values match Expo Router file segments under each tab.
 *
 * Keep this module free of React header chrome imports — `navigation.js` and
 * many Jest suites import it. Account/app menu header options are merged in
 * [`getDetailStackScreens`](./detailStackScreens.js).
 */

export const DETAIL_ROUTE_NAMES = Object.freeze({
  STOCK: "stock/[market]/[ticker]",
  SINGLE_ANALYSIS: "single-analysis",
  ANALYSES_LIST: "analyses-list",
  INSIDER_ANALYSIS: "insider-analysis",
  TREND_STRATEGY: "trend-strategy",
  MANUAL_STRATEGY: "manual-strategy/[id]",
  MEDIA_MONITOR: "media-monitor/[id]",
  NOTIFICATION_MESSAGE: "notification-message",
  NOTIFICATION_LIST: "notification-list",
  SID_DASHBOARD: "sid-dashboard",
  TREND_DASHBOARD: "trend-dashboard",
  SUPPLY_DEMAND_DASHBOARD: "supply-demand-dashboard",
  ACCOUNT_MENU: "account-menu",
  APP_MENU: "app-menu",
  SETTINGS: "settings",
  SEARCH: "search"
});

/**
 * Flat Stack.Screen registrations for tab layouts (stock + dashboards hide their own headers).
 * Prefer [`getDetailStackScreens`](./detailStackScreens.js) in layouts so account/app
 * menu get static transparent header options without coupling this file to UI.
 *
 * @type {ReadonlyArray<{ name: string, options?: { headerShown?: boolean, animation?: string } }>}
 */
export const DETAIL_STACK_SCREENS = Object.freeze([
  {
    name: DETAIL_ROUTE_NAMES.STOCK,
    options: { headerShown: false }
  },
  { name: DETAIL_ROUTE_NAMES.SINGLE_ANALYSIS },
  { name: DETAIL_ROUTE_NAMES.ANALYSES_LIST },
  { name: DETAIL_ROUTE_NAMES.INSIDER_ANALYSIS },
  { name: DETAIL_ROUTE_NAMES.TREND_STRATEGY },
  { name: DETAIL_ROUTE_NAMES.MANUAL_STRATEGY },
  { name: DETAIL_ROUTE_NAMES.MEDIA_MONITOR },
  { name: DETAIL_ROUTE_NAMES.NOTIFICATION_MESSAGE },
  { name: DETAIL_ROUTE_NAMES.NOTIFICATION_LIST },
  {
    name: DETAIL_ROUTE_NAMES.SID_DASHBOARD,
    options: { headerShown: false }
  },
  {
    name: DETAIL_ROUTE_NAMES.TREND_DASHBOARD,
    options: { headerShown: false }
  },
  {
    name: DETAIL_ROUTE_NAMES.SUPPLY_DEMAND_DASHBOARD,
    options: { headerShown: false }
  },
  {
    name: DETAIL_ROUTE_NAMES.ACCOUNT_MENU,
    options: { animation: "slide_from_left" }
  },
  { name: DETAIL_ROUTE_NAMES.APP_MENU },
  { name: DETAIL_ROUTE_NAMES.SETTINGS },
  { name: DETAIL_ROUTE_NAMES.SEARCH }
]);

/**
 * Path segments nested under the home tab (no `/home` prefix in the URL).
 * Used by getTabRoutePrefixForPathname to treat these as home-hosted.
 */
export const HOME_NESTED_DETAIL_SEGMENTS = Object.freeze([
  "stock",
  "sid-dashboard",
  "trend-dashboard",
  "supply-demand-dashboard",
  "manual-strategy",
  "media-monitor",
  "single-analysis",
  "analyses-list",
  "insider-analysis",
  "notification-message",
  "notification-list",
  "trend-strategy",
  "account-menu",
  "app-menu",
  "settings",
  "search"
]);

export const STRATEGY_DASHBOARD_PATH_SEGMENTS = Object.freeze([
  "sid-dashboard",
  "trend-dashboard",
  "supply-demand-dashboard"
]);
